import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import map from "lodash-es/map"

import type { TranslationObj } from "$/utils/lang"

import {
    availableCoachAdapter,
    extraPartiesAdapter,
    reflectionBaseAdapter,
    selfAssessmentBaseAdapter
} from "@/3514/adapters"
import type { IAvailableCoachDto } from "@/3514/types"
import { getSessionNameEnumFromType } from "@/3514/utils"

import { EParticipantOnboardingStep as EStep } from "./participant.onboarding.api.config"
import type {
    IParticipantOnboardingCoachStepCoachesQueryDto as ICoachStepCoachesQueryDto,
    IParticipantOnboardingCoachStepCoachesQueryModel as ICoachStepCoachesQueryModel,
    IParticipantOnboardingCoachStepPairingDto as ICoachStepPairingDto,
    IParticipantOnboardingCoachStepPairingModel as ICoachStepPairingModel,
    IParticipantOnboardingCoachStepPairingQueryDto as ICoachStepPairingQueryDto,
    IParticipantOnboardingCoachStepSessionQueryDto as ICoachStepSessionQueryDto,
    IParticipantOnboardingCoachStepSessionQueryModel as ICoachStepSessionQueryModel,
    IParticipantOnboardingDiagnosticStepQueryDto as IDiagnosticStepQueryDto,
    IParticipantOnboardingDiagnosticStepQueryModel as IDiagnosticStepQueryModel,
    IParticipantOnboardingHoldsStepQueryDto as IHoldsStepQueryDto,
    IParticipantOnboardingHoldsStepQueryModel as IHoldsStepQueryModel,
    IParticipantOnboardingHoldsSeriesDto as IHoldsStepSeriesDto,
    IParticipantOnboardingHoldsSeriesEventDto as IHoldsStepSeriesEventDto,
    IParticipantOnboardingHoldsSeriesEventModel as IHoldsStepSeriesEventModel,
    IParticipantOnboardingHoldsSeriesModel as IHoldsStepSeriesModel,
    IParticipantOnboardingReflectionStepQueryDto as IReflectionStepQueryDto,
    IParticipantOnboardingReflectionStepQueryModel as IReflectionStepQueryModel,
    IParticipantOnboardingStepsInfoQueryDto as IStepInfoQueryDto,
    IParticipantOnboardingStepsInfoQueryModel as IStepInfoQueryModel,
    IParticipantOnboardingVideoStepQueryDto as IVideoStepQueryDto,
    IParticipantOnboardingVideoStepQueryModel as IVideoStepQueryModel
} from "./participant.onboarding.api.types"

export default {
    stepInfo: (dto: IStepInfoQueryDto): IStepInfoQueryModel =>
        isEmpty(dto)
            ? { availableSteps: [], previousSteps: [] }
            : {
                  availableSteps: isEmpty(dto.available_steps)
                      ? []
                      : map<EStep, EStep>(dto.available_steps, (s: EStep): EStep => s),
                  previousSteps: isEmpty(dto.previous_steps)
                      ? []
                      : map<EStep, EStep>(dto.previous_steps, (s: EStep): EStep => s)
              },
    steps: {
        [EStep.Video]: (dto: IVideoStepQueryDto): IVideoStepQueryModel =>
            isEmpty(dto)
                ? null
                : {
                      text: isEmpty(dto.on_boarding_text) ? null : dto.on_boarding_text,
                      video: dto.on_boarding_video
                  },
        [EStep.Diagnostic]: (dtos: IDiagnosticStepQueryDto[]): IDiagnosticStepQueryModel[] =>
            isEmpty(dtos)
                ? []
                : map<IDiagnosticStepQueryDto, IDiagnosticStepQueryModel>(
                      dtos,
                      ({ module, ...rest }: IDiagnosticStepQueryDto): IDiagnosticStepQueryModel => ({
                          moduleId: module.id,
                          moduleTitle: module.title_data,
                          ...selfAssessmentBaseAdapter({ ...rest })
                      })
                  ),
        [EStep.Reflection]: (dtos: IReflectionStepQueryDto[]): IReflectionStepQueryModel[] =>
            isEmpty(dtos)
                ? []
                : map<IReflectionStepQueryDto, IReflectionStepQueryModel>(
                      dtos,
                      (dto: IReflectionStepQueryDto): IReflectionStepQueryModel => ({
                          order: dto.order,
                          type: dto.type,
                          questionData: isEmpty(dto.question_data)
                              ? null
                              : { ...reflectionBaseAdapter(dto.question_data), status: dto.question_data.status }
                      })
                  ),
        [EStep.Holds]: (dto: IHoldsStepQueryDto): IHoldsStepQueryModel =>
            isEmpty(dto)
                ? { series: [], minCoachingSessionsCount: null }
                : {
                      minCoachingSessionsCount: isNull(dto.min_coaching_sessions) ? 0 : dto.min_coaching_sessions,
                      series: isEmpty(dto.hold_series)
                          ? []
                          : map<IHoldsStepSeriesDto, IHoldsStepSeriesModel>(
                                dto.hold_series,
                                (s: IHoldsStepSeriesDto): IHoldsStepSeriesModel => ({
                                    id: s.id,
                                    events: isEmpty(s.hold_events)
                                        ? []
                                        : map<IHoldsStepSeriesEventDto, IHoldsStepSeriesEventModel>(
                                              s.hold_events,
                                              (e: IHoldsStepSeriesEventDto): IHoldsStepSeriesEventModel => ({
                                                  durationInMinutes: e.duration_minutes,
                                                  enrollmentRank: e.enrollment_rank,
                                                  dateTime: e.hold_datetime
                                              })
                                          )
                                })
                            )
                  },
        [EStep.Coach]: {
            session: (dto: ICoachStepSessionQueryDto): ICoachStepSessionQueryModel =>
                isEmpty(dto)
                    ? null
                    : {
                          durationInMin: dto.duration,
                          enrollmentRank: dto.enrollment_rank,
                          id: dto.id,
                          sessionName: getSessionNameEnumFromType(dto.session_type),
                          isCoachMatchingSkipped: dto.skip_coach_matching,
                          isExtraPartySession: dto.is_extra_party_session,
                          extraParties: extraPartiesAdapter(dto.extra_parties_info)
                      },
            pairing: (queryDto: ICoachStepPairingQueryDto): ICoachStepPairingModel[] =>
                isEmpty(queryDto)
                    ? null
                    : map<ICoachStepPairingDto, ICoachStepPairingModel>(
                          queryDto.results,
                          (dto: ICoachStepPairingDto): ICoachStepPairingModel => ({
                              answerOption: isEmpty(dto.answer_options)
                                  ? null
                                  : {
                                        id: dto.answer_options.id,
                                        internalName: dto.answer_options.internal_name,
                                        optionsLabels: map(
                                            dto.answer_options.options_data,
                                            ({ option_text: text }): TranslationObj => text
                                        ),
                                        optionsCount: dto.answer_options.options_number
                                    },
                              id: dto.id,
                              internalName: dto.internal_name,
                              order: dto.order,
                              question: dto.question_data,
                              uuid: dto.uuid
                          })
                      ),
            coaches: (dto: ICoachStepCoachesQueryDto): ICoachStepCoachesQueryModel[] =>
                isEmpty(dto)
                    ? []
                    : map<IAvailableCoachDto, ICoachStepCoachesQueryModel>(dto.results, availableCoachAdapter)
        }
    }
}
